import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Compass, Layers, Pencil, CalendarDays, Filter, LineChart, Sparkles, GitBranch, Settings as SettingsIcon, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useTheme } from "../lib/theme";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/posicionamento", icon: Compass, label: "Posicionamento" },
  { to: "/workspace", icon: Layers, label: "Workspace" },
  { to: "/conteudo", icon: Pencil, label: "Conteúdo" },
  { to: "/calendario", icon: CalendarDays, label: "Campanhas" },
  { to: "/funil", icon: Filter, label: "Funil & Métricas" },
  { to: "/performance", icon: LineChart, label: "Performance" },
  { to: "/frameworks", icon: Sparkles, label: "Frameworks" },
  { to: "/sistema", icon: GitBranch, label: "Mapa do Sistema" },
  { to: "/configuracoes", icon: SettingsIcon, label: "Configurações" },
];

function ThemeSwitcher() {
  const { theme, setTheme, themes } = useTheme();
  const glyphs = { principal: "◐", claro: "☀", escuro: "☾" };
  return (
    <div className="flex items-center gap-1 card-2 px-1.5 py-1.5" data-testid="theme-switcher">
      {themes.map((t) => (
        <button
          key={t}
          onClick={() => setTheme(t)}
          data-testid={`theme-${t}`}
          className={`px-2.5 py-1 rounded-md text-xs font-medium transition ${theme === t ? "grad-bg text-white" : "text-muted hover:text-[var(--txt)]"}`}
          title={t}
        >
          {glyphs[t]}
        </button>
      ))}
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, activeBrand, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }} data-testid="dashboard-layout">
      {/* Sidebar */}
      <aside className="w-64 border-r flex flex-col" style={{ background: "var(--panel)", borderColor: "var(--border)" }}>
        <div className="p-5 border-b" style={{ borderColor: "var(--border)" }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg grad-bg" />
            <div>
              <div className="font-bold text-sm">Social Booster</div>
              <div className="font-mono text-[10px] text-muted uppercase tracking-widest">v2.0</div>
            </div>
          </div>
        </div>

        {activeBrand && (
          <div className="p-4 border-b" style={{ borderColor: "var(--border)" }} data-testid="active-brand">
            <div className="text-[10px] uppercase tracking-widest text-muted mb-1 font-mono">Marca ativa</div>
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-lg grad-bg flex items-center justify-center text-white font-bold text-sm">
                {(activeBrand.name || "?").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{activeBrand.name}</div>
                <div className="flex items-center gap-1">
                  <span className={`chip ${activeBrand.source === "labrand" ? "grad" : ""}`} style={{ fontSize: 9, padding: "2px 6px" }}>
                    {activeBrand.source === "labrand" ? "integrado ao LABrand" : "manual"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-1" data-testid="sidebar-nav">
          {NAV.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.to;
            return (
              <Link key={item.to} to={item.to} className={`sidebar-item ${active ? "active" : ""}`} data-testid={`nav-${item.to.replace("/", "")}`}>
                <Icon size={17} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t" style={{ borderColor: "var(--border)" }}>
          <button
            onClick={async () => { await logout(); navigate("/login"); }}
            className="sidebar-item w-full"
            data-testid="logout-button"
          >
            <LogOut size={17} />
            <span>Sair</span>
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className="h-16 border-b flex items-center px-6 gap-4 justify-end" style={{ borderColor: "var(--border)", background: "var(--panel)" }}>
          <ThemeSwitcher />
          <div className="flex items-center gap-2 card-2 px-3 py-1.5" data-testid="user-badge">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
            ) : (
              <div className="w-7 h-7 rounded-full grad-bg flex items-center justify-center text-white text-xs font-bold">
                {(user?.name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <span className="text-sm hidden md:block">{user?.name}</span>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>
    </div>
  );
}
